import { useCallback, useRef, useState } from 'react';

type OnUnlockOptions =
  | {
      command: 'make-inactive';
    }
  | {
      command: 'activate';
      id: string;
    }
  | {
      command: 'activate-last-request';
    };

interface singletonContextProps {
  activeId: string | undefined;
  requestToMakeActive: (id: string) => void;
  makeInactive: (id?: string) => void;
  lock: (makeInactive?: boolean) => void;
  unlock: (options?: OnUnlockOptions) => void;
}

/**
 * Enforce that there is only ever one instance of something available at any given time.
 * Great for any UI element that should only have a single instance on screen, e.g. a hover
 * popover.
 */
export default function useSingletonContext(): [
  singletonContextProps,
  React.Dispatch<React.SetStateAction<string | undefined>>,
] {
  const [activeId, setActiveId] = useState<string>();
  const activeIdRef = useRef('');
  const isLockedRef = useRef(false);

  // This should keep track of the last field we had the intent to open. Any successful calls to unactivate will
  // reset this since the intent was to not have anything be active.
  const lastRequest = useRef<string>(undefined);

  const handleRequestToMakeActive = useCallback((id: string) => {
    lastRequest.current = id;
    if (isLockedRef.current) return;

    setActiveId(id);
    activeIdRef.current = id;
  }, []);

  const handleMakeInactive = useCallback((id?: string) => {
    if (isLockedRef.current) return;

    // We use a ref here instead of state because we want to be sure that even if this is triggered in the same
    // event bubbling sequence that we are looking at the actual active element id, and not a piece of old state
    // due to closures.
    if (activeIdRef.current === id || !id) {
      activeIdRef.current = '';
      setActiveId(undefined);
      lastRequest.current = undefined;
    }
  }, []);

  const handleLock = useCallback((makeInactive?: boolean) => {
    isLockedRef.current = true;

    if (makeInactive) {
      setActiveId(undefined);
    }
  }, []);

  const handleUnlock = useCallback((options?: OnUnlockOptions) => {
    isLockedRef.current = false;

    if (options?.command === 'activate') {
      setActiveId(options.id);
    } else if (options?.command === 'activate-last-request') {
      setActiveId(lastRequest.current);
    } else if (options?.command === 'make-inactive') {
      setActiveId(undefined);
    }
  }, []);

  return [
    {
      activeId,
      requestToMakeActive: handleRequestToMakeActive,
      makeInactive: handleMakeInactive,
      lock: handleLock,
      unlock: handleUnlock,
    },
    setActiveId,
  ];
}
